import { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { ScrollArea } from "../../ui/scroll-area";

import {
  Box,
  Database,
  Server,
  Key,
  Lock,
  Layers,
  FileText,
  HardDrive,
  Network,
  Container,
  Search,
  ChevronRight,
  ArrowLeft,
  ChevronLeft,
  SettingsIcon,
  Shield,
  Clock,
  Cloud,
  Globe,
  Gauge,
  RotateCcw,
} from "lucide-react";
import { SiAmazon, SiKubernetes } from "react-icons/si";
import RequestCloudAccessDialog from "./RequestCloudAccessDialog";
import { DetailsDialog } from "../Cloud/DetailsDialog";
import { CloudUsersPageProps } from "../Cloud/interface";
import useNotificationsStore from "../../../services/notificationStore";
import {
  CLOUD_REFRESH_INTERVAL_MS,
  formatTime,
  getAWSKubernetesList,
  getAzureKubernetesList,
  getAzureUserConfig,
  getGCPKubernetesList,
  getGCPUserConfig,
  getK8ApplicableRoles,
  getK8Resources,
  getK8ResourceTypes,
  haveSameRoles,
  resolveK8SubjectName,
} from "./Data";
import useUserStore from "../../../services/userStore";
import EmptyStateCard from "../EmptyStateCard";
import TenantCloudConfigureDialog from "./TenantCloudConfigureDialog";
import CloudFilters from "../CloudFilters";
import { configureGCPUser } from "./Api/tenantgcpapi";
import { configureUser } from "./Api/tenantazureapi";
import { configureAWSUser, fetchUserConfiguration } from "./Api/tenantawsapi";
import { myLocalStorage } from "../../StorageHelper";
import { FaGoogle } from "react-icons/fa6";
import { VscAzure } from "react-icons/vsc";
import {
  readPersistedListState,
  scopedListStateKey,
  SCOPED_LIST_STATE_PREFIX,
  writePersistedListState,
  parsePersistedNumber,
  parsePersistedString,
} from "../../../utils/listPaginationState";
import "../Cloud/CloudStatus.css";
import { revokeRequest } from "../ResourceRequests/Api/resourcerequestapi";

const ITEMS_PER_PAGE = 12;

const cloudConfig: Record<string, { icon: any; name: string; fullName: string; label: string }> = {
  gcp: { icon: FaGoogle, name: "GCP", fullName: "Google Cloud Platform", label: "GCP" },
  aws: { icon: SiAmazon, name: "AWS", fullName: "Amazon Web Services", label: "AWS" },
  azure: { icon: VscAzure, name: "Azure", fullName: "Microsoft Azure", label: "Azure" },
};

const resourceTypesConstants = [
  { id: "namespace", name: "Namespace", icon: Layers },
  { id: "secret", name: "Secret", icon: Lock },
  { id: "node", name: "Node", icon: Server },
  { id: "serviceaccount", name: "Service Account", icon: Key },
  { id: "deployment", name: "Deployment", icon: Container },
  { id: "pod", name: "Pod", icon: Box },
  { id: "service", name: "Service", icon: Network },
  { id: "config_map", name: "ConfigMap", icon: SettingsIcon },
  { id: "PERSISTENT_VOLUME", name: "Persistent Volume", icon: HardDrive },
  { id: "STORAGE_CLASS", name: "Storage Class", icon: Database },
  { id: "NETWORK_POLICY", name: "Network Policy", type: "NETWORK_POLICY", icon: Shield },
  { id: "Job", name: "Job", type: "JOB", icon: FileText },
  { id: "CRON_JOB", name: "Cron Job", type: "CRON_JOB", icon: Clock },
  { id: "REPLICA_SET", name: "Replica Set", type: "REPLICA_SET", icon: Layers },
  { id: "STATEFUL_SET", name: "Stateful Set", type: "STATEFUL_SET", icon: Cloud },
  { id: "DAEMON_SET", name: "Daemon Set", type: "DAEMON_SET", icon: Gauge },
  { id: "INGRESS", name: "Ingress", type: "INGRESS", icon: Globe },
];



const getStatusColor = (status: string) => {
  switch (status) {
    case "running":
      return "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20";
    case "available":
      return "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20";
    case "pending":
      return "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const getResourceTypeLabel = (type: string) => {
  switch (type) {
    case "namespace":
      return "Kubernetes Namespace";
    case "secret":
      return "Kubernetes Secret";
    case "node":
      return "Kubernetes Node";
    case "serviceaccount":
      return "Kubernetes Service Account";
    case "deployment":
      return "Kubernetes Deployment";
    case "pod":
      return "Kubernetes Pod";
    case "service":
      return "Kubernetes Service";
    case "configmap":
      return "Kubernetes ConfigMap";
    case "persistentvolume":
      return "Kubernetes Persistent Volume";
    case "storageclass":
      return "Kubernetes Storage Class";
    default:
      return "Kubernetes";
  }
};

export default function TenantKubernetes({ cloudProvider }: CloudUsersPageProps) {
  const k8StateKey = scopedListStateKey(SCOPED_LIST_STATE_PREFIX.tenantK8, cloudProvider);
  const savedK8State = readPersistedListState(k8StateKey);
  const selectedCloudAccountRef = useRef<string | null>(null);
  const currentClusterRef = useRef<any>(null);
  const [selectedTenant, setSelectedTenant] = useState("");
  const [cloudProviderChanges, setCloudProviderChanges] = useState<any>();
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null);
  const [selectedResourceType, setSelectedResourceType] = useState<string>(
    parsePersistedString(savedK8State.selectedResourceType, "NAMESPACE"),
  );
  const [searchQuery, setSearchQuery] = useState(parsePersistedString(savedK8State.searchQuery));
  const [typeSearchQuery, setTypeSearchQuery] = useState(parsePersistedString(savedK8State.typeSearchQuery));
  const [resourceSearchQuery, setResourceSearchQuery] = useState(parsePersistedString(savedK8State.resourceSearchQuery));
  const [currentPage, setCurrentPage] = useState(parsePersistedNumber(savedK8State.currentPage, 1));
  const [clusterPage, setClusterPage] = useState(parsePersistedNumber(savedK8State.clusterPage, 1));
  const [requestAccessDialogOpen, setRequestAccessDialogOpen] = useState(false);
  const [requestAccessResource, setRequestAccessResource] = useState<any>(null);
  const applicableRolesRequestRef = useRef(0);
  const applicableRolesLoadedRef = useRef(false);
  const [clusters, setClusters] = useState<any[]>([]);
  const [resourceTypes, setResourceTypes] = useState<any[]>([]);
  const [currentResources, setCurrentResources] = useState<any[]>([]);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [isApplicableRolesLoading, setIsApplicableRolesLoading] = useState(false);
  const { email } = useUserStore((state) => state.user);
  const [isConfigured, setIsConfigured] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configurationRevision, setConfigurationRevision] = useState(0);
  const [cloudConfigData, setCloudConfigData] = useState<any>({
    azureDisplayName: "",
    azureId: "",
    gcpUserName: "",
    gcpAccountEmail: "",
    awsCredentials: [],
  });
  const [upn, setUpn] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [resourceLoading, setResourceLoading] = useState(false);
  const {
    userK8Notifications,
    userK8ApprovedNotifications,
    setUserK8Notifications,
    setUserK8ApprovedNotifications,
  } = useNotificationsStore();
  const [revokingAccessIds, setRevokingAccessIds] = useState<Set<string>>(new Set());
  const config = cloudConfig[cloudProvider];
  const CloudIcon = config.icon;

  const handleCloudProvider = (value: any) => {
    const nextValue = value || "";
    if (selectedCloudAccountRef.current === nextValue) {
      return;
    }

    selectedCloudAccountRef.current = nextValue;
    setCloudProviderChanges(value);
    setSelectedCluster(null);
    setCurrentResources([]);
  };
  // Filter clusters by search
  const filteredClusters = clusters.filter((cluster) =>
    (cluster.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (cluster.region || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Cluster pagination
  const totalClusterPages = Math.ceil(filteredClusters.length / ITEMS_PER_PAGE);
  const clusterStartIndex = (clusterPage - 1) * ITEMS_PER_PAGE;
  const paginatedClusters = filteredClusters.slice(clusterStartIndex, clusterStartIndex + ITEMS_PER_PAGE);

  // Filter resource types by search
  const filteredResourceTypes = resourceTypes?.filter((type) => {
    const name = (type?.name || type?.value || "").toLowerCase();
    const query = typeSearchQuery?.toLowerCase?.() || "";
    return name.includes(query);
  });


  const currentCluster = clusters.find(c => c.id === selectedCluster);
  currentClusterRef.current = currentCluster;


  const searchFilteredResources = currentResources?.filter((resource) =>
    (resource.name || "").toLowerCase().includes(resourceSearchQuery.toLowerCase())
  );
  const selectedTypeLabel =
    resourceTypes.find(t => t.key === selectedResourceType || t.id === selectedResourceType)?.value ||
    resourceTypes.find(t => t.key === selectedResourceType || t.id === selectedResourceType)?.name ||
    "Resources";

  const totalPages = Math.ceil(searchFilteredResources.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedResources = searchFilteredResources.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  // Reset pagination when filters change
  const handleResourceTypeChange = (typeId: string) => {
    setSelectedResourceType(typeId);
    setCurrentPage(1);
    setResourceSearchQuery("");
  };

  const handleResourceSearch = (query: string) => {
    setResourceSearchQuery(query);
    setCurrentPage(1);
  };

  const handleClusterSearch = (query: string) => {
    setSearchQuery(query);
    setClusterPage(1);
  };

  useEffect(() => {
    writePersistedListState(k8StateKey, {
      clusterPage,
      currentPage,
      selectedResourceType,
      searchQuery,
      resourceSearchQuery,
      typeSearchQuery,
    });
  }, [k8StateKey, clusterPage, currentPage, selectedResourceType, searchQuery, resourceSearchQuery, typeSearchQuery]);

  const enterCluster = (clusterId: string) => {
    writePersistedListState(k8StateKey, {
      clusterPage,
      searchQuery,
      currentPage,
      selectedResourceType,
      resourceSearchQuery,
      typeSearchQuery,
    });
    setSelectedCluster(clusterId);
  };
  const parseAccessExpiry = (value: any) => {
    if (!value) return null;
    const timestamp =
      typeof value === "string" && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(value)
        ? `${value}Z`
        : value;
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const isAccessActive = (accessRecord: any) => {
    if (!accessRecord) return false;
    if (
      accessRecord?.privilegeLevel?.toUpperCase?.() === "PERMANENT" ||
      accessRecord?.expiryTimeAmount === 0
    ) {
      return true;
    }
    const expiry = parseAccessExpiry(accessRecord?.validTo);
    return expiry ? expiry.getTime() > Date.now() : false;
  };

  const fetchAccessGranted = (type: string, resource: any) => {
    const clusterResource = userK8ApprovedNotifications?.filter(
      (userK8ApprovedNotification: any) =>
        userK8ApprovedNotification.clusterName === currentCluster?.name &&
        userK8ApprovedNotification.policyResourceName === resource.name &&
        userK8ApprovedNotification.status === "APPROVED" &&
        isAccessActive(userK8ApprovedNotification)
    );
    return clusterResource || [];
  };

  const fetchPendingRequest = (resource: any) =>
    userK8Notifications?.filter(
      (notification: any) =>
        notification.clusterName === currentCluster?.name &&
        notification.policyResourceName === resource.name &&
        notification.status === "PENDING"
    ) || [];

  const handleRevokeAccess = async (accessRecords: any[], resourceId: string) => {
    const activeRecords = accessRecords.filter((record) => record?.id != null);
    if (activeRecords.length === 0) return;

    setRevokingAccessIds((current) => new Set(current).add(resourceId));
    try {
      for (const record of activeRecords) {
        const revoked = await revokeRequest("k8", record.id);
        if (revoked !== true) {
          throw new Error("Failed to revoke Kubernetes access");
        }
      }
      const revokedIds = new Set(activeRecords.map((record) => record.id));
      setUserK8ApprovedNotifications(
        userK8ApprovedNotifications.filter((notification: any) => !revokedIds.has(notification.id))
      );
      setUserK8Notifications(
        userK8Notifications.filter((notification: any) => !revokedIds.has(notification.id))
      );
    } finally {
      setRevokingAccessIds((current) => {
        const next = new Set(current);
        next.delete(resourceId);
        return next;
      });
    }
  };


  const getUserDetails = useCallback(async () => {
    if (!selectedCluster || !email) {
      return;
    }
    try {
      const resolved = await resolveK8SubjectName(cloudProvider, email);
      setUpn(resolved.upn);
      setDisplayName(resolved.displayName);
    } catch (e) {
      setUpn("");
      setDisplayName("");
    }
  }, [cloudProvider, email, selectedCluster]);

  const getResources = useCallback(async (options?: { live?: boolean }) => {
    const cluster = currentClusterRef.current;
    if (cluster && selectedResourceType) {
      if (!options?.live) {
        setResourceLoading(true);
      }
      try {
        let details = cluster.details
        let clusterId = cloudProvider === "aws" ? details.clusterArn : cloudProvider === "gcp" ? details.gcpClusterId : details.azureId
        let response = await getK8Resources(clusterId, selectedResourceType, cloudProvider);
        setCurrentResources(response)
      } finally {
        if (!options?.live) {
          setResourceLoading(false)
        }
      }
    }

  }, [cloudProvider, selectedResourceType])

  const getCurrentCluster = useCallback(async () => {
    // Show ALL published clusters returned by the list API (no per-user
    // approval gating). Users can then request access to specific resources
    // within a cluster from this page.
    if (cloudProvider === "gcp") {
      const response = await getGCPKubernetesList();
      setClusters(response || []);
    } else if (cloudProvider === "aws") {
      const response = await getAWSKubernetesList();
      setClusters(response || []);
    } else {
      const response = await getAzureKubernetesList();
      setClusters(response || []);
    }
  }, [cloudProvider]);

  const getUserCloudConfig = useCallback(async () => {
    if (cloudProvider === "gcp") {
      const response = await getGCPUserConfig(email);
      const hasConfig = Boolean(response?.gcpAccountEmail);
      setIsConfigured(hasConfig);
      setConfigDialogOpen(!hasConfig);
      return;
    }

    if (cloudProvider === "aws") {
      const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
      const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");
      if (!selectedAwsAccount) setConfigDialogOpen(true);
      const response = await fetchUserConfiguration(latestTenant, email);
      const matchedConfig = Array.isArray(response)
        ? (selectedAwsAccount
          ? response.find((item: any) => item.awsAccountId === selectedAwsAccount)
          : response[0])
        : response;
      const hasConfig = Boolean(matchedConfig?.awsUserName && matchedConfig?.awsAccountARN);
      setIsConfigured(hasConfig);
      setConfigDialogOpen(!hasConfig);
      return;
    }

    const response = await getAzureUserConfig(email);
    const hasConfig = Boolean(response?.azureId);
    setIsConfigured(hasConfig);
    setConfigDialogOpen(!hasConfig);
  }, [cloudProvider, email]);

  const submitUserReg = async (): Promise<boolean> => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    try {
      let response: any;
      if (cloudProvider === "gcp") {
        response = await configureGCPUser(
          latestTenant,
          email,
          cloudConfigData?.gcpUserName || "",
          cloudConfigData?.gcpAccountEmail
        );
      } else if (cloudProvider === "azure") {
        const payload = {
          azureId: cloudConfigData.azureId,
          displayName: cloudConfigData.displayName || cloudConfigData.azureDisplayName,
          wsTenantName: latestTenant,
          email,
        };
        response = await configureUser(payload);
      } else {
        const payload = {
          tenantName: latestTenant,
          tenantEmail: email,
          users: cloudConfigData.awsCredentials,
        };
        response = await configureAWSUser(payload);
        const configurations = Array.isArray(response) ? response : response ? [response] : [];
        const userConfig = configurations[0];
        if (userConfig?.awsAccountId) {
          myLocalStorage.setItem("selectedAwsAccount", userConfig.awsAccountId);
        }
      }

      if (!response || (Array.isArray(response) && response.length === 0)) return false;
      void Promise.allSettled([getCurrentCluster()]);
      return true;
    } catch {
      return false;
    }
  };

  const handleConfigured = () => {
    setIsConfigured(true);
    setConfigurationRevision((revision) => revision + 1);
  };


  useEffect(() => {

    const fetchCluster = async () => {
      try {
        await getCurrentCluster();
      } catch (error) {
        console.error("Error fetching project resources:", error);
      }
    };

    fetchCluster();

  }, [cloudProvider, cloudProviderChanges, getCurrentCluster])

  useEffect(() => {
    const checkUserConfig = async () => {
      try {
        await getUserCloudConfig();
      } catch (err) {
        console.error("Error checking user config:", err);
        setIsConfigured(false);
        setConfigDialogOpen(true);
      }
    };
    checkUserConfig();
  }, [getUserCloudConfig, cloudProviderChanges, selectedTenant]);


  const refetchApplicableRoles = useCallback(async (options?: { live?: boolean }) => {
    if (!requestAccessDialogOpen || !requestAccessResource) {
      applicableRolesLoadedRef.current = false;
      setAvailableRoles((current) => current.length === 0 ? current : []);
      setIsApplicableRolesLoading(false);
      return;
    }
    const isInitialLoad = !applicableRolesLoadedRef.current;
    if (options?.live && isInitialLoad) return;

    const requestId = ++applicableRolesRequestRef.current;
    if (isInitialLoad) {
      setAvailableRoles([]);
      setIsApplicableRolesLoading(true);
    }
    try {
      const payload = {
        wsTenantName: requestAccessResource.wsTenantName,
        clusterId: requestAccessResource.clusterId,
        cloudProviderType: requestAccessResource.cloudProviderType,
        type: selectedResourceType,
        resourceAccountId: requestAccessResource.cloudResourceAccountId,
        namespace: requestAccessResource.namespace,
      };
      const roles: any[] = await getK8ApplicableRoles(requestAccessResource.name, payload);
      if (applicableRolesRequestRef.current !== requestId) return;
      applicableRolesLoadedRef.current = true;
      setAvailableRoles((current) => haveSameRoles(current, roles) ? current : roles);
    } catch (error) {
      if (applicableRolesRequestRef.current !== requestId) return;
      if (isInitialLoad) {
        applicableRolesLoadedRef.current = true;
        setAvailableRoles([]);
      }
      console.error("Error fetching applicable roles:", error);
    } finally {
      if (isInitialLoad && applicableRolesRequestRef.current === requestId) {
        setIsApplicableRolesLoading(false);
      }
    }
  }, [requestAccessDialogOpen, requestAccessResource, selectedResourceType]);

  useEffect(() => {
    refetchApplicableRoles();
  }, [refetchApplicableRoles]);


  useEffect(() => {
    if (selectedCluster && selectedResourceType) {
      const fetchClusterResources = async () => {
        try {
          await getResources();
        } catch (error) {
          console.error("Error fetching project resources:", error);
        }
      };

      fetchClusterResources();
    }

  }, [cloudProvider, selectedCluster, selectedResourceType, getResources])
  const getResourceTypes = useCallback(async () => {
    const iconMap = Object.fromEntries(
      resourceTypesConstants.map(({ id, icon }) => [id.toUpperCase(), icon])
    );
    let response = await getK8ResourceTypes();
    const mergedResourceTypes = response.map((type: any) => ({
      ...type,
      icon: iconMap[type.key] || FileText,
    }));
    setResourceTypes(mergedResourceTypes)
  }, [])

  useEffect(() => {
    if (selectedCluster) {
      getResourceTypes();
    }

  }, [cloudProvider, selectedCluster, getResourceTypes])

  useEffect(() => {
    if (selectedCluster) {
      getUserDetails();
    }
  }, [cloudProvider, selectedCluster, getUserDetails]);

  useEffect(() => {
    if (requestAccessDialogOpen && currentCluster) {
      getUserDetails();
    }
  }, [requestAccessDialogOpen, currentCluster, getUserDetails]);

  useEffect(() => {
    if (!isConfigured) return;
    let refreshing = false;
    const refresh = async () => {
      if (refreshing) return;
      refreshing = true;
      try {
        const requests: Promise<any>[] = [getCurrentCluster(), refetchApplicableRoles({ live: true })];
        if (selectedCluster) {
          requests.push(getResources({ live: true }), getResourceTypes(), getUserDetails());
        }
        await Promise.all(requests);
      } catch (error) {
        console.error("Error refreshing Kubernetes resources:", error);
      } finally {
        refreshing = false;
      }
    };
    const timer = setInterval(refresh, CLOUD_REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [getCurrentCluster, getResourceTypes, getResources, getUserDetails, isConfigured, refetchApplicableRoles, selectedCluster]);


  if (!isConfigured) {
    return (
      <div className="h-full flex flex-col bg-background overflow-auto">
        <div className="p-6 space-y-6">
          <Card className="cloud-card-surface rounded-2xl">
            <CardContent className="p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <CloudIcon className="w-6 h-6 text-primary" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl font-semibold text-foreground">{config.name} Kubernetes</h1>
                  <p className="text-sm text-muted-foreground">Configure your cloud identity before requesting Kubernetes access.</p>
                </div>
              </div>
              <CloudFilters
                key={`${cloudProvider}-${configurationRevision}`}
                cloudProvider={cloudProvider}
                onTenantChange={setSelectedTenant}
                onCloudAccountChange={handleCloudProvider}
                isAdmin={false}
                hideTenant
                inline
              />
            </CardContent>
          </Card>

          <EmptyStateCard
            icon={SiKubernetes}
            title="No User Configured"
            description={`Please configure your ${config.name} account first.`}
            action={
              <Button onClick={() => setConfigDialogOpen(true)}>
                Configure
              </Button>
            }
          />

          {configDialogOpen && (
            <TenantCloudConfigureDialog
              open={configDialogOpen}
              onOpenChange={setConfigDialogOpen}
              cloudProvider={cloudProvider}
              config={config}
              cloudConfigData={cloudConfigData}
              setCloudConfigData={setCloudConfigData}
              onConfigured={handleConfigured}
              subUserReg={submitUserReg}
            />
          )}
        </div>
      </div>
    );
  }

  if (!selectedCluster) {
    return (
      <div className="h-full flex flex-col bg-background overflow-auto">
        <div className="p-6 space-y-6">
          <Card className="cloud-card-surface rounded-2xl">
            <CardContent className="p-3 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search clusters by name, region, or group..."
                  value={searchQuery}
                  onChange={(e) => handleClusterSearch(e.target.value)}
                  className="k8s-search-input pl-10"
                  data-testid="input-search-clusters"
                />
              </div>
              <CloudFilters
                key={`${cloudProvider}-${configurationRevision}`}
                cloudProvider={cloudProvider}
                onTenantChange={setSelectedTenant}
                onCloudAccountChange={handleCloudProvider}
                isAdmin={false}
                hideTenant
                inline
              />
            </CardContent>
          </Card>

          {filteredClusters.length === 0 && searchQuery === "" ? (
            <Card className="cloud-card-surface border-dashed rounded-2xl">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <SiKubernetes className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No Clusters Available</h3>
                <p className="text-sm text-muted-foreground max-w-md">
                  There are no published Kubernetes clusters for this account yet.
                </p>
              </CardContent>
            </Card>
          ) : paginatedClusters.length === 0 ? (
            <Card className="cloud-card-surface border-dashed rounded-2xl">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <SiKubernetes className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No Clusters Found</h3>
                <p className="text-sm text-muted-foreground max-w-md">
                  No clusters match your search. Try adjusting your search terms.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginatedClusters.map((cluster) => (
                  <Card
                    key={cluster.id}
                    className="cloud-list-card cursor-pointer transition-all rounded-2xl flex flex-col"
                    onClick={() => enterCluster(cluster.id)}
                    data-testid={`card-cluster-${cluster.id}`}
                  >
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <SiKubernetes className="w-6 h-6 text-primary" />
                        </div>
                        {cluster.type !== "-" && (
                          <Badge variant="secondary" className="text-xs rounded-md">
                            {cluster.type}
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="flex flex-col flex-1">
                      <div className="flex-1 space-y-4">
                        <div>
                          <h3 className="font-medium text-sm mb-1 break-all">
                            {cluster.name}
                          </h3>
                        </div>

                        <div className="space-y-2 pt-2 border-t text-xs">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-muted-foreground">Region:</span>
                            <span className="font-medium text-right">{cluster.region || "-"}</span>
                          </div>
                        </div>
                      </div>

                      <Button
                        className="w-full gap-2 text-primary hover:text-primary mt-4"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          enterCluster(cluster.id);
                        }}
                        data-testid={`button-cluster-${cluster.id}`}
                      >
                        View Resources
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {totalClusterPages > 1 && (
                <div className="cloud-card-surface rounded-2xl flex items-center justify-between p-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {clusterStartIndex + 1}-{Math.min(clusterStartIndex + ITEMS_PER_PAGE, filteredClusters.length)} of {filteredClusters.length}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setClusterPage(p => Math.max(1, p - 1))}
                      disabled={clusterPage === 1}
                      data-testid="button-cluster-prev-page"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <div className="text-sm">
                      Page {clusterPage} of {totalClusterPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setClusterPage(p => Math.min(totalClusterPages, p + 1))}
                      disabled={clusterPage === totalClusterPages}
                      data-testid="button-cluster-next-page"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background overflow-auto">
      <div className="p-6 space-y-6">
        <Card className="cloud-card-surface rounded-2xl">
          <CardContent className="p-3">
            <div className="flex flex-wrap items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  const saved = readPersistedListState(k8StateKey);
                  setSelectedCluster(null);
                  setClusterPage(parsePersistedNumber(saved.clusterPage, clusterPage));
                  setSearchQuery(parsePersistedString(saved.searchQuery, searchQuery));
                }}
                className="h-10 w-10 shrink-0 rounded-[10px] border border-border/50 bg-background/70 text-muted-foreground shadow-sm hover:bg-background/90 hover:text-foreground"
                data-testid="button-back-to-clusters"
                aria-label="Back to clusters"
                title="Back to clusters"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="flex min-w-0 items-center gap-2">
                <SiKubernetes className="w-6 h-6 shrink-0 text-primary" />
                <h2 className="truncate font-semibold text-lg">{currentCluster?.name}</h2>
                <span className="whitespace-nowrap text-sm text-muted-foreground">({currentCluster?.region})</span>
              </div>
              <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
                <CloudFilters
                  cloudProvider={cloudProvider}
                  onTenantChange={setSelectedTenant}
                  onCloudAccountChange={handleCloudProvider}
                  isAdmin={false}
                  hideTenant
                  inline
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="k8s-detail-shell">
          <div className="w-64 k8s-panel flex flex-col flex-shrink-0">
            <div className="p-3 k8s-panel-header">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search types..."
                  value={typeSearchQuery}
                  onChange={(e) => setTypeSearchQuery(e.target.value)}
                  className="k8s-search-input pl-10"
                  data-testid="input-search-types"
                />
              </div>
            </div>

            <div className="flex-1 overflow-auto k8s-panel-list">
              <div className="space-y-1">
                {filteredResourceTypes?.map((type) => {
                  const isSelected = selectedResourceType === type.key;
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.key}
                      onClick={() => handleResourceTypeChange(type.key)}
                      className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${isSelected
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-background/50"
                        }`}
                      data-testid={`button-type-${type.key}`}
                    >
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="font-medium truncate">{type.value}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex-1 k8s-panel flex flex-col">
            <div className="p-4 k8s-panel-header">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{selectedTypeLabel}</h3>
                  <span className="text-sm text-muted-foreground">
                    ({searchFilteredResources.length} {searchFilteredResources.length === 1 ? "item" : "items"})
                  </span>
                </div>
              </div>
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search resources..."
                  value={resourceSearchQuery}
                  onChange={(e) => handleResourceSearch(e.target.value)}
                  className="k8s-search-input pl-10"
                  data-testid="input-search-resources"
                />
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div className="p-4">
                {resourceLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <Card key={index} className="cloud-card-surface rounded-2xl">
                        <CardContent className="p-5 space-y-4">
                          <div className="w-12 h-12 rounded-md bg-muted animate-pulse" />
                          <div className="h-4 w-2/3 rounded-md bg-muted animate-pulse" />
                          <div className="h-3 w-full rounded-md bg-muted animate-pulse" />
                          <div className="h-9 w-full rounded-md bg-muted animate-pulse" />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : paginatedResources.length === 0 ? (
                  <Card className="cloud-card-surface border-dashed rounded-2xl">
                    <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                        <SiKubernetes className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">
                        {resourceSearchQuery ? "No Resources Found" : "No Published Resources Available"}
                      </h3>
                      <p className="text-sm text-muted-foreground max-w-md">
                        {resourceSearchQuery
                          ? "No resources match your search. Try adjusting your search terms."
                          : "You currently don't have published resources to access in this cluster."}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {paginatedResources.map((resource) => {
                        const typeInfo =
                          resourceTypes.find(t => t.id === resource.type || t.key === resource.type || t.key === selectedResourceType) ||
                          resourceTypes[0];
                        const Icon = typeInfo?.icon || FileText;
                        const status = selectedResourceType.toLowerCase().includes("node")
                          || selectedResourceType.toLowerCase().includes("deployement")
                          || selectedResourceType.toLowerCase().includes("pod") ? "running" : "available";
                        const isHasAccess = fetchAccessGranted(selectedResourceType || "", resource);
                        const isPendingRequest = fetchPendingRequest(resource).length > 0;

                        return (
                          <Card
                            key={resource.id}
                            className="cloud-list-card rounded-2xl transition-all flex flex-col"
                            data-testid={`card-resource-${resource.id}`}
                          >
                            <CardHeader className="pb-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  <Icon className="w-6 h-6 text-primary" />
                                </div>
                                <Badge
                                  variant="secondary"
                                  className={`text-xs ${getStatusColor(status)}`}
                                >
                                  {status}
                                </Badge>
                              </div>
                            </CardHeader>
                            <CardContent className="flex flex-col flex-1">
                              <div className="flex-1 space-y-3">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <h3 className="font-medium text-sm break-all flex-1" title={resource.name}>
                                      {resource.name}
                                    </h3>
                                    <DetailsDialog
                                      data={resource}
                                      title={`${resource.name} Details`}
                                      trigger="icon"
                                      testId={`button-details-${resource.id}`}
                                    />
                                  </div>
                                </div>

                                <div className="space-y-2 text-xs">
                                  <div className="flex justify-between gap-2">
                                    <span className="text-muted-foreground">Cluster:</span>
                                    <span className="font-medium truncate">{resource.cluster || currentCluster?.name || "-"}</span>
                                  </div>
                                  <div className="flex justify-between gap-2">
                                    <span className="text-muted-foreground">Namespace:</span>
                                    <span className="font-medium truncate">{resource.namespace || "Cluster-wide"}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="mt-4 pt-4 border-t">
                                {isHasAccess && isHasAccess.length > 0 ? (
                                  <div className="space-y-2">
                                    <div className="flex items-center justify-center gap-2 p-3 rounded-md bg-green-500/10 border border-green-500/20">
                                      <Clock className="w-4 h-4 text-green-600 dark:text-green-400" />
                                      <div className="flex flex-col items-center">
                                        <span className="text-xs text-muted-foreground">Access Granted</span>
                                        <span className="text-sm font-semibold text-green-600 dark:text-green-400 font-mono">
                                          {formatTime(isHasAccess, "kubernetes")}
                                        </span>
                                      </div>
                                    </div>
                                    <Button
                                      className="w-full gap-2 rounded-xl !border-transparent bg-red-600 text-white shadow-md shadow-red-600/20 hover:bg-red-700 hover:shadow-lg hover:shadow-red-600/30 focus-visible:ring-0"
                                      size="sm"
                                      disabled={revokingAccessIds.has(String(resource.id))}
                                      onClick={() => handleRevokeAccess(isHasAccess, String(resource.id))}
                                      data-testid={`button-revoke-access-${resource.id}`}
                                    >
                                      Revoke
                                      <RotateCcw className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    className={isPendingRequest
                                      ? "w-full gap-2 rounded-xl bg-muted text-muted-foreground"
                                      : "w-full gap-2 rounded-xl bg-primary text-primary-foreground shadow-md shadow-primary/20 hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/30"}
                                    size="sm"
                                    variant={isPendingRequest ? "secondary" : "default"}
                                    disabled={isPendingRequest}
                                    onClick={() => {
                                      applicableRolesRequestRef.current += 1;
                                      applicableRolesLoadedRef.current = false;
                                      setAvailableRoles([]);
                                      setIsApplicableRolesLoading(true);
                                      setRequestAccessResource(resource);
                                      setRequestAccessDialogOpen(true);
                                    }}
                                    data-testid={`button-request-${resource.id}`}
                                  >
                                    {isPendingRequest ? "Request Pending" : "Request Access"}
                                    {!isPendingRequest && <ChevronRight className="w-4 h-4" />}
                                  </Button>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>

                    {totalPages > 1 && (
                      <div className="cloud-card-surface rounded-2xl flex items-center justify-between mt-6 p-4">
                        <div className="text-sm text-muted-foreground">
                          Showing {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, searchFilteredResources.length)} of {searchFilteredResources.length}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            data-testid="button-prev-page"
                          >
                            <ChevronLeft className="w-4 h-4" />
                            Previous
                          </Button>
                          <div className="text-sm">
                            Page {currentPage} of {totalPages}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            data-testid="button-next-page"
                          >
                            Next
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>

      <RequestCloudAccessDialog
        open={requestAccessDialogOpen}
        onOpenChange={(open) => {
          setRequestAccessDialogOpen(open);
          if (!open) {
            applicableRolesRequestRef.current += 1;
            applicableRolesLoadedRef.current = false;
            setAvailableRoles([]);
            setIsApplicableRolesLoading(false);
          }
        }}
        resourceName={requestAccessResource?.name || ""}
        resourceType="kubernetes"
        resourceTypeLabel={requestAccessResource ? getResourceTypeLabel(requestAccessResource.type) : undefined}
        availableRoles={availableRoles}
        isRolesLoading={isApplicableRolesLoading}
        resource={requestAccessResource}
        upn={upn || ""}
        displayName={displayName || ""}
        clustername={currentCluster?.name || ""}
        type={selectedResourceType}
      />
    </div>
  );
}
